import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import {
  Boxes,
  ChevronDown,
  ChevronRight,
  LogOut,
  Plus,
  UserCircle,
  UserCog,
  LinkIcon,
  X,
} from "lucide-react";
import ADMIN_PATHS from "../ADMIN_PATHS";
import { navCategories } from "./adminNavConfig";

const ACCENT = "#D2042D";
const SIDEBAR_SCROLL_KEY = "adminSidebarScrollTop";
const cx = (...arr) => arr.filter(Boolean).join(" ");

// navCategories + advancedOptions come from adminNavConfig to keep sidebar and header search in sync.

export default function Sidebar({ mobileOpen = false, onClose }) {
  const location = useLocation();
  const user = { name: "Admin User", role: "Super Admin" };
  const scrollRef = useRef(null);

  // Open category if current route belongs to it; otherwise open first.
  const defaultOpen = useMemo(() => {
    const path = location.pathname;
    const match = navCategories.find((c) => c.links?.some((l) => path.startsWith(l.to)));
    return match?.heading || navCategories[0]?.heading;
  }, [location.pathname]);

  const [openCategories, setOpenCategories] = useState([defaultOpen]);

  useEffect(() => {
    if (!defaultOpen) return;
    setOpenCategories([defaultOpen]);
  }, [defaultOpen]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const saved = sessionStorage.getItem(SIDEBAR_SCROLL_KEY);
    if (saved) {
      el.scrollTop = Number(saved) || 0;
    }
  }, [location.pathname]);

  const toggleCategory = (heading) => {
    setOpenCategories((prev) => (prev[0] === heading ? [] : [heading]));
  };

  const handleNavigate = () => {
    if (scrollRef.current) {
      sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(scrollRef.current.scrollTop));
    }
    if (onClose) onClose();
  };

  const SidebarContent = () => (
    <>
      {/* Top */}
      <div className="px-4 pt-4 pb-3 border-b border-slate-200">
        <div className="flex items-center justify-between gap-2 md:hidden">
          <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-900">Menu</span>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-md border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center"
            aria-label="Close sidebar"
          >
            <X size={16} className="text-slate-700" />
          </button>
        </div>
        <Link
          to={ADMIN_PATHS.DASHBOARD}
          onClick={handleNavigate}
          className="flex items-center gap-3 rounded-md border border-slate-200 bg-white px-3 py-3 hover:bg-slate-50 transition-colors"
        >
          <div className="h-10 w-10 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center">
            <UserCog size={18} className="text-slate-700" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="h-4 w-[3px] rounded-full" style={{ background: ACCENT }} />
              <p className="text-sm font-semibold tracking-tight text-slate-900 truncate">
                Cherrfy Admin
              </p>
            </div>
            <p className="mt-0.5 text-xs font-medium text-slate-700 truncate">Operations Console</p>
          </div>
        </Link>
      </div>

      {/* Scroll area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 custom-scrollbar">
        <nav className="flex flex-col gap-3">
          {navCategories.map((cat) => {
            const isOpen = openCategories.includes(cat.heading);
            const CatIcon = cat.icon || Boxes;
            return (
              <div key={cat.heading} className="rounded-md border border-slate-200 bg-white overflow-hidden">
                <button
                  className={cx(
                    "w-full flex items-center gap-3 px-3 py-3 text-left transition-colors",
                    "hover:bg-slate-50/60"
                  )}
                  onClick={() => toggleCategory(cat.heading)}
                  aria-expanded={isOpen}
                >
                  <div className="h-9 w-9 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center">
                    <CatIcon size={18} className="text-slate-700" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-900">
                      {cat.heading}
                    </p>
                    <p className="mt-0.5 text-xs font-medium text-slate-700">
                      {cat.links?.length || 0} items
                    </p>
                  </div>

                  <div className="h-9 w-9 rounded-md bg-slate-900 text-white flex items-center justify-center transition-colors hover:bg-black">
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </div>
                </button>

                <div
                  className={cx(
                    "transition-[max-height] duration-300 overflow-hidden",
                    isOpen ? "max-h-[1000px]" : "max-h-0"
                  )}
                >
                  <div className="px-2 pb-2">
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
                      <div className="flex flex-col gap-1">
                        {cat.links.map((l) => (
                          <SidebarNavLink
                            key={l.to}
                            to={l.to}
                            icon={l.icon}
                            label={l.label}
                            onNavigate={handleNavigate}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Primary CTA */}
          <div className="pt-1">
            <Link
              to={ADMIN_PATHS.PRODUCTS.ALIEXPRESS}
              onClick={handleNavigate}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-semibold text-white bg-slate-900 hover:bg-black transition-colors"
            >
              <Plus size={16} />
              Add Product
            </Link>
            <p className="mt-2 text-xs font-medium text-slate-700 px-1">
              Create a product quickly without leaving the admin flow.
            </p>
          </div>

          {/* External */}
          <Link
            to={ADMIN_PATHS.STORE}
            target="_blank"
            onClick={handleNavigate}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors"
          >
            <LinkIcon size={16} className="text-slate-700" />
            Visit Store
          </Link>
        </nav>
      </div>

      {/* Bottom user */}
      <div className="px-4 py-4 border-t border-slate-200 bg-white">
        <div className="flex items-center gap-3 rounded-md border border-slate-200 bg-white px-3 py-3">
          <div className="h-10 w-10 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center">
            <UserCircle className="w-6 h-6 text-slate-700" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900 truncate">{user.name}</p>
            <p className="text-xs font-medium text-slate-700 truncate">{user.role}</p>
          </div>
          <button
            className="h-10 w-10 rounded-md border border-slate-200 bg-white hover:bg-slate-50 transition-colors flex items-center justify-center"
            title="Logout"
          >
            <LogOut className="w-5 h-5 text-slate-700" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile drawer */}
      <div
        className={cx(
          "md:hidden fixed inset-0 z-40",
          mobileOpen ? "pointer-events-auto" : "pointer-events-none"
        )}
        aria-hidden={!mobileOpen}
      >
        <div
          className={cx(
            "absolute inset-0 bg-slate-900/40 transition-opacity",
            mobileOpen ? "opacity-100" : "opacity-0"
          )}
          onClick={onClose}
        />
        <aside
          className={cx(
            "absolute inset-y-0 left-0 w-[292px] max-w-[85vw] bg-white border-r border-slate-200 flex flex-col overflow-hidden transition-transform",
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <SidebarContent />
        </aside>
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed top-0 left-0 h-screen w-[292px] bg-white border-r border-slate-200 flex-col overflow-hidden">
        <SidebarContent />
      </aside>
    </>
  );
}

function SidebarNavLink({ to, icon: Icon, label, onNavigate }) {
  // Use NavLink for correct active state.
  const isRoot = to === "/";
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }) => {
        const active = isRoot ? isActive : isActive; // keep standard behavior
        return cx(
          "group flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors",
          "text-sm font-semibold tracking-tight text-slate-900",
          active
            ? "border"
            : "border border-transparent hover:border-slate-200 hover:bg-white",
          active ? "" : ""
        );
      }}
      style={({ isActive }) =>
        isActive
          ? {
              borderColor: `${ACCENT}22`,
              background: `${ACCENT}10`,
              color: "#0f172a",
            }
          : undefined
      }
      end={false}
    >
      {({ isActive }) => (
        <>
          <span
            className={cx("h-8 w-8 rounded-md flex items-center justify-center border transition-colors", isActive
              ? ""
              : "border-slate-200 bg-white group-hover:bg-slate-50")}
            style={
              isActive
                ? { borderColor: `${ACCENT}22`, background: `${ACCENT}10` }
                : undefined
            }
          >
            <Icon
              size={18}
              className={cx(isActive ? "text-slate-900" : "text-slate-700")}
              style={isActive ? { color: ACCENT } : undefined}
            />
          </span>

          <span className="flex-1 min-w-0 truncate">{label}</span>

          {/* Active marker */}
          <span
            className={cx("h-2 w-2 rounded-full")}
            style={{ background: isActive ? ACCENT : "transparent" }}
          />
        </>
      )}
    </NavLink>
  );
}
